/**
 * Fully synthesized WebAudio SFX, no asset files, ported from unbox-lab: the one sound engine every
 * three.js opener plays through. Same lifecycle rules as `SoundPlayer`: one lazy AudioContext per
 * page, resumed from a user gesture, every failure path silent. Muted whenever the host turned the
 * opener's sounds off (`OpenerSoundChannel.enabled`).
 *
 * Voices play through a **bus**: its own level and glue compressor, optionally a soft saturator and
 * a generated-impulse room reverb that voices send to (`wet`), and `cut()` to silence everything
 * in flight on it. `sfx.tone` / `sfx.noise` and the vocabulary below play on the default bus (Neon
 * Slam's and Neon Pack's mix); a kind with a mix of its own asks for one (`sfx.bus`, Retail Box).
 */
import type { Rarity } from "./item";
import { midi } from "./midi";

type Wave = OscillatorType;

/**
 * The envelope every voice shares. With `dur` the voice lasts `dur` seconds from an exponential
 * attack; with `decay` it instead takes a linear `attack` to its peak and `decay` seconds from the
 * peak back to silence (Retail Box's envelope). `decay` wins when both are set.
 */
interface VoiceEnv {
  /** Seconds from now. */
  when?: number;
  attack?: number;
  dur?: number;
  decay?: number;
  vol?: number;
  /** Stereo position -1..1. */
  pan?: number;
  /** Reverb send 0..1, on a bus that has a reverb. */
  wet?: number;
  /** Seconds a `to` glide takes; defaults to the voice's length (`dur`, or `decay`). */
  glide?: number;
}

export interface ToneOpts extends VoiceEnv {
  freq: number;
  /** Glide to this frequency (exponential). */
  to?: number;
  type?: Wave;
  detune?: number;
  /** A lowpass on the voice, Hz. */
  filter?: number;
  /** Vibrato: rate (Hz) and depth (cents). */
  vib?: [number, number];
}

export interface NoiseOpts extends VoiceEnv {
  type?: BiquadFilterType;
  freq?: number;
  /** Sweep the filter to this frequency (exponential). */
  to?: number;
  q?: number;
}

/** How a bus is mixed: its level into its compressor, and its optional color. */
export interface BusOptions {
  level: number;
  /** The glue compressor; an unset field keeps the browser's default. */
  compressor: {
    threshold: number;
    ratio: number;
    knee?: number;
    attack?: number;
    release?: number;
  };
  /** A tanh soft-clip on the dry voices, this hard; none when omitted. */
  saturation?: number;
  /** A room of `seconds` of decaying stereo noise, returned at `level`, for voices' `wet` sends. */
  reverb?: { seconds: number; level: number };
}

/** The default bus: Neon Slam's and Neon Pack's mix. */
const DEFAULT_BUS: BusOptions = { level: 0.8, compressor: { threshold: -14, ratio: 4 } };

// Major-pentatonic-ish ladder used for arpeggios / pitched hits.
const SCALE = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24];

function saturationCurve(amount: number): Float32Array<ArrayBuffer> {
  const n = 1024;
  const curve = new Float32Array(n);
  const k = Math.tanh(amount);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * amount) / k;
  }
  return curve;
}

/** A room: a stereo impulse of decaying noise, darkened toward its tail. */
function roomImpulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const ir = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const k = i / len;
      lp += (Math.random() * 2 - 1 - lp) * (0.9 - 0.75 * k);
      d[i] = lp * (1 - k) ** 2.6;
    }
  }
  return ir;
}

/** The page's one AudioContext, its shared noise, and the mute and volume every bus ends in. */
class AudioEngine {
  ctx: AudioContext | null = null;
  noiseBuf: AudioBuffer | null = null;
  /** After every bus's compressor: 1, or 0 while muted. */
  mute: GainNode | null = null;
  /** After the mute: the shopper's volume, 0 to 1. A muted engine stays at 0 whatever it is. */
  volumeNode: GainNode | null = null;
  muted = false;
  volume = 1;
  private failed = false;
  private listening = false;

  /**
   * Resumes the context on any gesture on the page, not only on the opener's own stage. Installed
   * once, the first time something uses the engine, rather than on import.
   */
  listen() {
    if (this.listening || typeof window === "undefined") return;
    this.listening = true;
    const unlock = () => this.unlock();
    window.addEventListener("pointerdown", unlock, { capture: true });
    window.addEventListener("keydown", unlock, { capture: true });
  }

  ensure(): AudioContext | null {
    if (this.ctx || this.failed) return this.ctx;
    this.listen();
    try {
      const ctx = new AudioContext();
      const mute = ctx.createGain();
      mute.gain.value = this.muted ? 0 : 1;
      const volume = ctx.createGain();
      volume.gain.value = this.volume;
      mute.connect(volume).connect(ctx.destination);
      const len = ctx.sampleRate * 2;
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.ctx = ctx;
      this.mute = mute;
      this.volumeNode = volume;
      this.noiseBuf = buf;
    } catch {
      this.ctx = null;
      this.failed = true;
    }
    return this.ctx;
  }

  unlock() {
    const ctx = this.ensure();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  }

  setMuted(m: boolean) {
    this.muted = m;
    this.listen();
    if (this.mute && this.ctx)
      this.mute.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.02);
  }

  /** Sets the shopper's volume (0 to 1); a non-finite value is ignored, the rest is clamped. */
  setVolume(gain: number) {
    if (!Number.isFinite(gain)) return;
    this.volume = Math.min(1, Math.max(0, gain));
    if (this.volumeNode && this.ctx)
      this.volumeNode.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
  }

  /** The context, when a voice may play now. Suspended contexts queue nodes and play on resume. */
  ready(): AudioContext | null {
    const ctx = this.ensure();
    if (!ctx || this.muted || ctx.state === "closed") return null;
    return ctx;
  }
}

/** One mix voices play through; its nodes are built the first time a voice plays. */
export class SfxBus {
  private out: GainNode | null = null;
  private input: GainNode | null = null;
  private verb: GainNode | null = null;

  constructor(
    private readonly engine: AudioEngine,
    private readonly opts: BusOptions,
  ) {}

  private build(ctx: AudioContext): boolean {
    if (this.out) return true;
    const mute = this.engine.mute;
    if (!mute) return false;
    const { compressor: c } = this.opts;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = c.threshold;
    comp.ratio.value = c.ratio;
    if (c.knee !== undefined) comp.knee.value = c.knee;
    if (c.attack !== undefined) comp.attack.value = c.attack;
    if (c.release !== undefined) comp.release.value = c.release;
    const out = ctx.createGain();
    out.gain.value = this.opts.level;
    out.connect(comp).connect(mute);
    if (this.opts.reverb) {
      const conv = ctx.createConvolver();
      conv.buffer = roomImpulse(ctx, this.opts.reverb.seconds);
      const verb = ctx.createGain();
      verb.gain.value = this.opts.reverb.level;
      verb.connect(conv).connect(out);
      this.verb = verb;
    }
    this.out = out;
    this.input = this.newInput(ctx);
    return true;
  }

  /** The node dry voices enter: through the saturator, when the bus has one, into its level. */
  private newInput(ctx: AudioContext): GainNode | null {
    if (!this.out) return null;
    const input = ctx.createGain();
    if (this.opts.saturation) {
      const shaper = ctx.createWaveShaper();
      shaper.curve = saturationCurve(this.opts.saturation);
      shaper.oversample = "2x";
      input.connect(shaper).connect(this.out);
    } else {
      input.connect(this.out);
    }
    return input;
  }

  /**
   * Fades out every dry voice playing or scheduled on this bus (a skip, an unmount) and starts a
   * fresh input. Reverb tails ring out.
   */
  cut() {
    const ctx = this.engine.ctx;
    const input = this.input;
    if (!ctx || !input) return;
    input.gain.setTargetAtTime(0, ctx.currentTime, 0.03);
    setTimeout(() => input.disconnect(), 400);
    this.input = this.newInput(ctx);
  }

  private ready(): AudioContext | null {
    const ctx = this.engine.ready();
    if (!ctx || !this.build(ctx) || !this.input) return null;
    return ctx;
  }

  /**
   * Envelope, pan and reverb send for one voice: its input node, start, end and glide time.
   * `attack` is the default attack of the `dur` envelope.
   */
  private chain(
    ctx: AudioContext,
    e: VoiceEnv,
    attack: number,
  ): { input: GainNode; t: number; end: number; glide: number } | null {
    const dest = this.input;
    if (!dest) return null;
    const t = ctx.currentTime + (e.when ?? 0);
    const vol = e.vol ?? 0.3;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    let end: number;
    let span: number;
    if (e.decay !== undefined) {
      const a = Math.max(0.001, e.attack ?? 0.003);
      end = t + a + e.decay;
      span = e.decay;
      g.gain.linearRampToValueAtTime(vol, t + a);
    } else {
      span = e.dur ?? 0.2;
      end = t + span;
      g.gain.exponentialRampToValueAtTime(vol, t + (e.attack ?? attack));
    }
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    let node: AudioNode = g;
    if (e.pan) {
      const p = ctx.createStereoPanner();
      p.pan.value = e.pan;
      g.connect(p);
      node = p;
    }
    node.connect(dest);
    if (e.wet && this.verb) {
      const send = ctx.createGain();
      send.gain.value = e.wet;
      node.connect(send).connect(this.verb);
    }
    return { input: g, t, end, glide: e.glide ?? span };
  }

  tone(o: ToneOpts) {
    const ctx = this.ready();
    if (!ctx) return;
    const c = this.chain(ctx, o, 0.004);
    if (!c) return;
    const osc = ctx.createOscillator();
    osc.type = o.type ?? "sine";
    osc.frequency.setValueAtTime(o.freq, c.t);
    if (o.to) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.to), c.t + c.glide);
    if (o.detune) osc.detune.value = o.detune;
    if (o.vib) {
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = o.vib[0];
      depth.gain.value = o.vib[1];
      lfo.connect(depth).connect(osc.detune);
      lfo.start(c.t);
      lfo.stop(c.end + 0.05);
    }
    let src: AudioNode = osc;
    if (o.filter) {
      const f = ctx.createBiquadFilter();
      f.type = "lowpass";
      f.frequency.value = o.filter;
      osc.connect(f);
      src = f;
    }
    src.connect(c.input);
    osc.start(c.t);
    osc.stop(c.end + 0.05);
  }

  noise(o: NoiseOpts) {
    const ctx = this.ready();
    const buf = this.engine.noiseBuf;
    if (!ctx || !buf) return;
    const c = this.chain(ctx, o, 0.005);
    if (!c) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = o.type ?? "bandpass";
    f.frequency.setValueAtTime(o.freq ?? 1000, c.t);
    if (o.to) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), c.t + c.glide);
    f.Q.value = o.q ?? 1;
    src.connect(f).connect(c.input);
    src.start(c.t, Math.random() * 1.5);
    src.stop(c.end + 0.05);
  }
}

class Sfx {
  private readonly engine = new AudioEngine();
  private readonly main = new SfxBus(this.engine, DEFAULT_BUS);
  private lastTick = 0;
  private listeners = new Set<() => void>();

  get isMuted() {
    return this.engine.muted;
  }

  /** Mutes every bus on the page. */
  setMuted(m: boolean) {
    this.engine.setMuted(m);
    for (const l of this.listeners) l();
  }

  /** Scales every bus on the page, 0 to 1 (muted stays silent whatever it is). */
  setVolume(gain: number) {
    this.engine.setVolume(gain);
  }

  subscribe(fn: () => void) {
    this.engine.listen();
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Call from any user gesture. */
  unlock() {
    this.engine.unlock();
  }

  /** A bus of its own on the page's one context, for a kind whose mix differs. */
  bus(opts: BusOptions): SfxBus {
    return new SfxBus(this.engine, opts);
  }

  tone(o: ToneOpts) {
    this.main.tone(o);
  }

  noise(o: NoiseOpts) {
    this.main.noise(o);
  }

  // ── Vocabulary ────────────────────────────────────────────────────────

  /** Reel/cycle tick, throttled so fast spins don't machine-gun. */
  tick(pitch = 1, vol = 0.18) {
    const now = performance.now();
    if (now - this.lastTick < 28) return;
    this.lastTick = now;
    this.tone({ freq: 2100 * pitch, to: 1400 * pitch, type: "triangle", dur: 0.035, vol });
    this.noise({ freq: 5000, q: 2, dur: 0.02, vol: vol * 0.6, type: "highpass" });
  }

  click(vol = 0.2) {
    this.tone({ freq: 900, to: 500, type: "square", dur: 0.03, vol: vol * 0.5, filter: 3000 });
  }

  pop(pitch = 1, vol = 0.25) {
    this.tone({ freq: 380 * pitch, to: 900 * pitch, type: "sine", dur: 0.09, vol });
  }

  whoosh(dur = 0.4, vol = 0.25, up = true) {
    this.noise({
      dur,
      vol,
      type: "bandpass",
      freq: up ? 400 : 2600,
      to: up ? 2600 : 350,
      q: 1.2,
      attack: dur * 0.5,
    });
  }

  thud(vol = 0.6) {
    this.tone({ freq: 120, to: 38, type: "sine", dur: 0.45, vol });
    this.noise({ dur: 0.18, vol: vol * 0.5, type: "lowpass", freq: 600, to: 120 });
  }

  slam(vol = 0.9) {
    this.tone({ freq: 90, to: 28, type: "sine", dur: 0.9, vol });
    this.tone({ freq: 180, to: 50, type: "triangle", dur: 0.25, vol: vol * 0.4 });
    this.noise({ dur: 0.5, vol: vol * 0.55, type: "lowpass", freq: 2400, to: 150 });
    this.noise({ dur: 0.08, vol: vol * 0.4, type: "highpass", freq: 3000 });
  }

  crack(vol = 0.5) {
    this.noise({ dur: 0.07, vol, type: "highpass", freq: 2200, q: 0.7 });
    this.noise({ dur: 0.2, vol: vol * 0.5, type: "bandpass", freq: 900, to: 300, q: 3 });
    this.tone({ freq: 220, to: 70, type: "triangle", dur: 0.12, vol: vol * 0.5 });
  }

  shatter(vol = 0.6) {
    for (let i = 0; i < 9; i++) {
      this.tone({
        freq: 2400 + Math.random() * 3000,
        type: "sine",
        dur: 0.15 + Math.random() * 0.35,
        vol: vol * 0.12,
        when: i * 0.025 + Math.random() * 0.03,
        pan: Math.random() * 1.6 - 0.8,
      });
    }
    this.noise({ dur: 0.5, vol: vol * 0.5, type: "highpass", freq: 3500, to: 1500 });
    this.thud(vol * 0.6);
  }

  tear(vol = 0.3) {
    for (let i = 0; i < 5; i++) {
      this.noise({
        dur: 0.04 + Math.random() * 0.04,
        vol: vol * (0.5 + Math.random() * 0.5),
        type: "bandpass",
        freq: 1800 + Math.random() * 2500,
        q: 1.5,
        when: i * 0.02,
      });
    }
  }

  ratchet(vol = 0.25) {
    this.noise({ dur: 0.03, vol, type: "bandpass", freq: 2600, q: 5 });
    this.tone({ freq: 300, to: 180, type: "square", dur: 0.03, vol: vol * 0.3, filter: 1800 });
  }

  /** Pitched blip; `step` walks the pentatonic scale (plinko pegs, taps). */
  blip(step = 0, vol = 0.2, type: Wave = "triangle") {
    const n = 72 + (SCALE[((step % SCALE.length) + SCALE.length) % SCALE.length] ?? 0);
    this.tone({ freq: midi(n), type, dur: 0.14, vol });
  }

  /** Continuous rising tension, `dur` seconds. */
  riser(dur = 1.5, vol = 0.18, from = 180, to = 1400) {
    this.tone({ freq: from, to, type: "sawtooth", dur, vol, attack: dur * 0.8, filter: 2200 });
    this.noise({
      dur,
      vol: vol * 0.7,
      type: "bandpass",
      freq: 500,
      to: 5000,
      q: 2,
      attack: dur * 0.85,
    });
  }

  sparkle(count = 6, vol = 0.1) {
    for (let i = 0; i < count; i++) {
      this.tone({
        freq: midi(84 + (SCALE[Math.floor(Math.random() * SCALE.length)] ?? 0)),
        type: "sine",
        dur: 0.25,
        vol,
        when: i * 0.06 + Math.random() * 0.03,
        pan: Math.random() * 1.4 - 0.7,
      });
    }
  }

  coin(vol = 0.2) {
    this.tone({ freq: midi(88), type: "square", dur: 0.08, vol: vol * 0.5, filter: 5000 });
    this.tone({
      freq: midi(95),
      type: "square",
      dur: 0.3,
      vol: vol * 0.5,
      filter: 5000,
      when: 0.07,
    });
  }

  /** Rarity-tiered reveal stinger (loot-os: bad / rare / mega prize → six tiers here). */
  reveal(r: Rarity) {
    const base = 60 + r * 2;
    const chord = [0, 4, 7, 12, 16, 19];
    const notes = chord.slice(0, 2 + Math.min(r, 4));
    const gap = r >= 4 ? 0.07 : 0.09;
    notes.forEach((n, i) => {
      this.tone({
        freq: midi(base + n),
        type: "triangle",
        dur: 0.5 + r * 0.12,
        vol: 0.18,
        when: i * gap,
      });
      if (r >= 2)
        this.tone({ freq: midi(base + n + 12), type: "sine", dur: 0.6, vol: 0.07, when: i * gap });
    });
    if (r >= 3) {
      // Pad swell under the arpeggio.
      for (const d of [-8, 8]) {
        this.tone({
          freq: midi(base),
          type: "sawtooth",
          dur: 1.6,
          vol: 0.06,
          attack: 0.3,
          detune: d,
          filter: 1400,
        });
        this.tone({
          freq: midi(base + 7),
          type: "sawtooth",
          dur: 1.6,
          vol: 0.05,
          attack: 0.3,
          detune: d,
          filter: 1400,
        });
      }
      this.sparkle(6 + r * 2, 0.06);
    }
    if (r >= 4) {
      this.tone({ freq: 70, to: 35, type: "sine", dur: 1.2, vol: 0.6 });
      this.noise({ dur: 1.8, vol: 0.12, type: "highpass", freq: 6000, attack: 0.05 });
      for (const n of [24, 28, 31, 36]) {
        this.tone({
          freq: midi(base + n),
          type: "sine",
          dur: 1.4,
          vol: 0.05,
          when: 0.35,
          attack: 0.2,
        });
      }
    }
    if (r >= 5) {
      this.slam(0.5);
      this.riser(0.6, 0.1, 600, 3000);
      for (let i = 0; i < 3; i++) this.sparkle(8, 0.05);
    }
  }

  /** Short ominous "tier-up" hint for teasers (overwatch beams, chest glow). */
  hint(r: Rarity) {
    this.tone({ freq: midi(55 + r * 3), type: "sine", dur: 0.5, vol: 0.2, attack: 0.02 });
    this.tone({ freq: midi(67 + r * 3), type: "triangle", dur: 0.4, vol: 0.08, when: 0.03 });
  }
}

export const sfx = new Sfx();
